import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import LogoMark from "./LogoMark";
import { useLang } from "../i18n/LanguageContext";
import { scrollToId, lenisRef } from "../lib/scroll";

const ABOUT_LABEL = { en: "About", no: "Om" };

export default function Nav() {
  const { lang, setLang, t } = useLang();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (lenisRef.current) {
      if (open) lenisRef.current.stop();
      else lenisRef.current.start();
    }
  }, [open]);

  const onHome = pathname === "/";
  const light = onHome && !scrolled && !open;
  const linkTone = light ? "text-cream/85 hover:text-cream" : "text-umber hover:text-espresso";
  const barTone = light ? "bg-cream" : "bg-espresso";

  const goSection = (id) => {
    setOpen(false);
    if (onHome) scrollToId(id);
    else navigate("/", { state: { scrollTo: id } });
  };
  const goHome = () => {
    setOpen(false);
    if (onHome) scrollToId("top");
    else navigate("/");
  };
  const goAbout = () => {
    setOpen(false);
    if (onHome) navigate("/about");
    else {
      window.scrollTo(0, 0);
      if (lenisRef.current) lenisRef.current.scrollTo(0, { immediate: true });
    }
  };

  return (
    <header
      className={`fixed inset-x-0 top-0 z-[70] transition-all duration-500 ${
        scrolled ? "border-b border-clay/60 bg-paper/95 backdrop-blur-sm" : "border-b border-transparent bg-transparent"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 sm:px-8">
        <button
          data-testid="nav-home"
          onClick={goHome}
          className="flex flex-shrink-0 items-center gap-3"
          aria-label="The Things We Keep"
        >
          <LogoMark className="h-10 w-auto" tone={light ? "#FBF8F0" : "#8A4F3D"} dot={light ? "#D4A373" : "#D4A373"} />
          <span
            className={`whitespace-nowrap font-serif text-lg tracking-wide transition-colors duration-500 ${
              light ? "text-cream" : "text-espresso"
            }`}
          >
            The Things We Keep
          </span>
        </button>

        <nav className="hidden items-center gap-6 lg:flex">
          <button
            data-testid="nav-link-story"
            onClick={() => goSection("story")}
            className={`link-underline text-xs uppercase tracking-[0.18em] transition-colors duration-500 ${linkTone}`}
          >
            {t.nav.story}
          </button>
          <button
            data-testid="nav-link-gallery"
            onClick={() => goSection("gallery")}
            className={`link-underline text-xs uppercase tracking-[0.18em] transition-colors duration-500 ${linkTone}`}
          >
            {t.nav.gallery}
          </button>
          <button
            data-testid="nav-link-about"
            onClick={goAbout}
            className={`link-underline text-xs uppercase tracking-[0.18em] transition-colors duration-500 ${
              pathname === "/about" ? "text-terracotta" : linkTone
            }`}
          >
            {ABOUT_LABEL[lang]}
          </button>
          <button
            data-testid="nav-link-process"
            onClick={() => navigate("/process")}
            className={`link-underline text-xs uppercase tracking-[0.18em] transition-colors duration-500 ${
              pathname === "/process" ? "text-terracotta" : linkTone
            }`}
          >
            Process &amp; Investment
          </button>
        </nav>

        <div className="flex items-center gap-3 sm:gap-4">
          <button
            data-testid="nav-cta"
            onClick={() => goSection("commission")}
            className={`hidden whitespace-nowrap rounded-full px-5 py-2.5 text-[11px] uppercase tracking-[0.18em] transition-colors duration-300 sm:inline-flex ${
              light ? "bg-cream text-espresso hover:bg-ochre" : "bg-terracotta text-cream hover:bg-espresso"
            }`}
          >
            {t.nav.commission}
          </button>
          <button
            data-testid="nav-menu-toggle"
            onClick={() => setOpen(!open)}
            aria-label="Menu"
            aria-expanded={open}
            className="flex flex-col items-start p-2 lg:hidden"
          >
            <span
              className={`block h-px w-6 transition-all duration-300 ${barTone} ${open ? "translate-y-[3.5px] rotate-45" : ""}`}
            />
            <span
              className={`mt-[7px] block h-px w-6 transition-all duration-300 ${barTone} ${
                open ? "-translate-y-[3.5px] -rotate-45" : ""
              }`}
            />
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            key="mobile-menu"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="fixed inset-0 z-[65] flex flex-col justify-center bg-paper px-8 lg:hidden"
            data-testid="nav-mobile-menu"
          >
            <nav className="flex flex-col gap-8">
              <button
                data-testid="nav-mobile-story"
                onClick={() => goSection("story")}
                className="text-left font-serif text-4xl font-light text-espresso transition-colors duration-300 hover:text-terracotta"
              >
                {t.nav.story}
              </button>
              <button
                data-testid="nav-mobile-gallery"
                onClick={() => goSection("gallery")}
                className="text-left font-serif text-4xl font-light text-espresso transition-colors duration-300 hover:text-terracotta"
              >
                {t.nav.gallery}
              </button>
              <button
                data-testid="nav-mobile-about"
                onClick={goAbout}
                className={`text-left font-serif text-4xl font-light transition-colors duration-300 hover:text-terracotta ${
                  pathname === "/about" ? "text-terracotta" : "text-espresso"
                }`}
              >
                {ABOUT_LABEL[lang]}
              </button>
              <button
                data-testid="nav-mobile-process"
                onClick={() => {
                  setOpen(false);
                  navigate("/process");
                }}
                className={`text-left font-serif text-4xl font-light transition-colors duration-300 hover:text-terracotta ${
                  pathname === "/process" ? "text-terracotta" : "text-espresso"
                }`}
              >
                Process &amp; Investment
              </button>
            </nav>
            <button
              data-testid="nav-mobile-cta"
              onClick={() => goSection("commission")}
              className="mt-14 self-start rounded-full bg-terracotta px-9 py-4 text-[11px] uppercase tracking-[0.2em] text-cream transition-colors duration-300 hover:bg-espresso"
            >
              {t.nav.commission}
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
